import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Command, LogOut, Menu, Moon, Octagon, PlayCircle, Sun, Wallet, WifiOff } from "lucide-react";
import { Badge, TradingModeBadge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Tooltip } from "../ui/Tooltip";
import { useConfirm } from "../ui/ConfirmDialog";
import { useToast } from "../ui/Toast";
import { useTheme } from "../../context/ThemeContext";
import { autotradeHalt } from "../../api";
import { riskBand } from "../../lib/riskMath";
import { findNavItem } from "./navigation";

const inr = (n) => `₹${Math.abs(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function IconAction({ label, onClick, children }) {
  return (
    <Tooltip content={label} position="bottom">
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="flex h-9 w-9 items-center justify-center rounded-xl border border-line bg-surface-2 text-low transition-colors hover:border-cyan-500/40 hover:text-hi"
      >
        {children}
      </button>
    </Tooltip>
  );
}

/* Global kill switch. Engaging is the safe direction: one click, no prompt.
 * Releasing re-arms entries, so it keeps the same confirmation the Autotrader
 * page asks for. Same endpoint as that page's toggle (POST /autotrade/halt). */
function HaltButton({ halted, isPaper, brokerLabel, onChanged }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const engage = async () => {
    setBusy(true);
    try {
      await autotradeHalt(true);
      toast.warning("HALTED — no new entries in any book. Open positions are still managed.");
      onChanged?.({ kill_switch: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not engage the kill switch — use the Autotrader page");
    }
    setBusy(false);
  };

  const release = async () => {
    if (!(await confirm({
      title: "Release the kill switch?",
      message: `New entries resume on the next cycle${isPaper ? " (paper mode)" : ` — in LIVE mode, with real money on ${brokerLabel}`}.`,
      confirmLabel: "Release kill switch",
    }))) return;
    setBusy(true);
    try {
      await autotradeHalt(false);
      toast.success("Kill switch released — entries resume on the next cycle");
      onChanged?.({ kill_switch: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not release the kill switch");
    }
    setBusy(false);
  };

  if (halted) {
    return (
      <Tooltip content="Kill switch is ON: no new entries in any book. Click to release (asks first)." position="bottom">
        <button
          type="button"
          onClick={release}
          disabled={busy}
          aria-label="Release kill switch"
          className="flex h-9 items-center gap-1.5 rounded-xl border border-rose-500/60 bg-rose-600 px-2.5 text-[10px] font-black uppercase tracking-label text-white shadow-[0_0_16px_-4px_rgb(var(--c-rose-500)/.8)] disabled:opacity-60"
        >
          <PlayCircle className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Halted ·&nbsp;</span>Release
        </button>
      </Tooltip>
    );
  }
  return (
    <Tooltip content="HALT: block every new entry (paper and live) at once. Open positions keep their stops. One click, no prompt." position="bottom">
      <button
        type="button"
        onClick={engage}
        disabled={busy}
        aria-label="Halt all new entries"
        className="flex h-9 items-center gap-1.5 rounded-xl border border-rose-500/50 bg-rose-500/[.1] px-2.5 text-[10px] font-black uppercase tracking-label text-rose-300 transition-colors hover:bg-rose-600 hover:text-white disabled:opacity-60"
      >
        <Octagon className="h-3.5 w-3.5" />
        Halt
      </button>
    </Tooltip>
  );
}

export function TopBar({
  onOpenMobileNav,
  onOpenCommand,
  traderStatus,
  auth,
  funds,
  brokerLabel,
  onBrokerLogin,
  onLogout,
  onStatusPatch,
  offline = false,
}) {
  const { pathname } = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const page = findNavItem(pathname);

  const running = !!traderStatus?.autopilot_running;
  const isPaper = traderStatus?.is_paper ?? true;
  const halted = !!traderStatus?.kill_switch;
  // open_trades carries BOTH books once the shell has /autotrade/positions
  // (see App.jsx); riskBand counts live rows separately so a LIVE position
  // can never hide behind a PAPER header.
  const band = riskBand(traderStatus);
  const pnl = traderStatus?.today_pnl || 0;
  const pnlUp = pnl >= 0;
  const fundsKnown = funds?.ready && Number.isFinite(funds?.available);
  const fundsTip = fundsKnown
    ? `Funds free for a new F&O order at ${funds.label || funds.broker} — the figure its order ticket shows · as of ${funds.as_of} IST`
    : `Funds at ${funds?.label || brokerLabel} unavailable${funds?.error ? ` — ${funds.error}` : ""}`;
  const bandTip = `Loss budget left = max daily loss − today's losses in the ${isPaper ? "PAPER" : "LIVE"} book (realised + open, every strategy — the figure the risk engine blocks entries on). Open risk = Σ (entry − effective stop) × qty over every open row, paper and live${band.live ? ` · ${band.live} LIVE` : ""}.`;

  return (
    <header className="topbar-edge sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-line bg-surface/80 px-3 backdrop-blur-xl sm:gap-3 sm:px-4 lg:px-6">
      <button
        type="button"
        onClick={onOpenMobileNav}
        aria-label="Open navigation"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-line bg-surface-2 text-low hover:text-hi lg:hidden"
      >
        <Menu className="h-4 w-4" />
      </button>

      {/* Where am I */}
      <div className="min-w-0 flex-1">
        <p className="hidden text-[10px] font-bold uppercase tracking-eyebrow text-faint sm:block">
          {page?.group || "Infinity"}
        </p>
        <h2 className="truncate text-sm font-bold text-hi sm:text-[15px]">{page?.label || "Infinity"}</h2>
      </div>

      {/* Live session state. The group may SHRINK (its tail is clipped) but never pushes the
          header wider than the window: a too-wide header made the whole page scroll sideways
          and slide under the fixed sidebar. The pills themselves keep their size, and the
          order is by importance — PAPER/LIVE and HALT sit at the front, never clipped. */}
      <div className="flex min-w-0 items-center gap-2 overflow-x-clip [&>*]:shrink-0">
        {offline && (
          <Tooltip content="The panel cannot reach the backend — figures below are the last values received" position="bottom">
            <span className="flex items-center gap-2 rounded-full border border-amber-500/35 bg-amber-500/[.1] px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-label text-amber-300">
              <WifiOff className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Backend offline</span>
            </span>
          </Tooltip>
        )}

        <Tooltip content={running ? "Autopilot is evaluating rules and may place orders" : "Autopilot is stopped — no automated orders"} position="bottom">
          <span
            className={`hidden items-center gap-2 rounded-full border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-label sm:flex ${
              running
                ? "border-emerald-500/30 bg-emerald-500/[.1] text-emerald-300"
                : "border-line-strong bg-surface-2 text-low"
            }`}
          >
            <span className="relative flex h-2 w-2">
              {running && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />}
              <span className={`relative inline-flex h-2 w-2 rounded-full ${running ? "bg-emerald-400" : "bg-slate-600"}`} />
            </span>
            <span className="hidden md:inline">Autopilot</span>
            <span>{running ? "On" : "Off"}</span>
          </span>
        </Tooltip>

        {/* PAPER / LIVE: visible at EVERY width — it is the one bit of state a
            trader must never have to scroll or resize to see. */}
        <Tooltip content={isPaper ? "PAPER: simulated fills, no broker orders" : `LIVE: real orders on ${brokerLabel} with real money`} position="bottom">
          <span className="inline-flex">
            <TradingModeBadge isPaper={isPaper} halted={halted} size="md" />
          </span>
        </Tooltip>

        {traderStatus && (
          <HaltButton halted={halted} isPaper={isPaper} brokerLabel={brokerLabel} onChanged={onStatusPatch} />
        )}

        {band.open > 0 && (
          <Tooltip content={`${band.open} open across both books${band.live ? ` — ${band.live} LIVE, ${band.paper} paper` : " (all paper)"}`} position="bottom">
            <span className={`${band.live ? "inline-flex" : "hidden md:inline-flex"} items-center gap-1.5 rounded-full border border-cyan-500/25 bg-cyan-500/[.08] px-3 py-1.5 text-xs font-semibold text-cyan-300`}>
              {band.open} open
              {band.live > 0 && (
                <span className="rounded bg-rose-600 px-1 py-px text-[9px] font-black uppercase text-white">{band.live} live</span>
              )}
            </span>
          </Tooltip>
        )}

        <Tooltip content={`Today P&L · all books: realised + unrealised of every strategy (rules + manual), net of charges — ${isPaper ? "PAPER" : "LIVE"} mode rows only`} position="bottom">
          <span
            className={`hidden items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-bold tabular-nums sm:inline-flex ${
              pnlUp ? "border-emerald-500/25 bg-emerald-500/[.08] text-emerald-300" : "border-rose-500/25 bg-rose-500/[.08] text-rose-300"
            }`}
          >
            {pnlUp ? "+" : "−"}{inr(pnl)}
            <span className="hidden text-[9px] font-semibold uppercase tracking-label opacity-70 min-[2000px]:inline">all books</span>
          </span>
        </Tooltip>

        {/* Risk band: the two numbers that decide whether the next trade may happen. */}
        {(band.budgetLeft != null || band.open > 0) && (
          <Tooltip content={bandTip} position="bottom">
            <span className="hidden items-center gap-2 rounded-full border border-line bg-surface-2 px-3 py-1.5 text-[10px] font-semibold tabular-nums text-low lg:inline-flex">
              <span>
                <span className="uppercase tracking-label text-faint">Budget<span className="hidden min-[2000px]:inline"> left</span> </span>
                <span className={`font-bold ${band.budgetLeft === 0 ? "text-rose-300" : "text-hi"}`}>{band.budgetLeft == null ? "—" : inr(band.budgetLeft)}</span>
              </span>
              <span className="h-3 w-px bg-line" />
              <span>
                <span className="uppercase tracking-label text-faint"><span className="hidden min-[2000px]:inline">Open </span>Risk </span>
                <span className={`font-bold ${band.openRisk > 0 ? "text-amber-300" : "text-hi"}`}>{inr(band.openRisk)}</span>
              </span>
            </span>
          </Tooltip>
        )}

        {traderStatus?.current_time_ist && (
          <span className="hidden text-xs font-medium tabular-nums text-faint min-[2000px]:inline">
            {traderStatus.current_time_ist} IST
          </span>
        )}
      </div>

      <div className="mx-1 hidden h-6 w-px bg-line sm:block" />

      {/* Session controls */}
      <div className="flex shrink-0 items-center gap-2">
        <span className="hidden sm:inline-flex">
          <IconAction label="Command palette (⌘K)" onClick={onOpenCommand}>
            <Command className="h-4 w-4" />
          </IconAction>
        </span>

        <IconAction label={isDark ? "Switch to light theme" : "Switch to dark theme"} onClick={toggleTheme}>
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </IconAction>

        {auth?.authenticated && funds && (
          <Tooltip content={fundsTip} position="bottom">
            <span
              className={`hidden items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-xs font-bold tabular-nums sm:flex ${
                fundsKnown ? "border-line bg-surface-2 text-hi" : "border-amber-500/30 bg-amber-500/[.08] text-amber-300"
              }`}
            >
              <Wallet className="h-3.5 w-3.5 shrink-0 text-low" />
              <span className="hidden text-[10px] font-bold uppercase tracking-label text-faint min-[2000px]:inline">Funds</span>
              {fundsKnown ? inr(funds.available) : "—"}
            </span>
          </Tooltip>
        )}

        {auth?.authenticated ? (
          <Tooltip content={`Broker session active — ${auth.user_name || auth.user_id}`} position="bottom">
            <span className="hidden min-w-0 items-center gap-2 rounded-xl border border-line bg-surface-2 py-1.5 pl-2.5 pr-2 md:flex">
              <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-400" />
              <span className="max-w-32 truncate text-xs font-semibold text-mid">{auth.user_name || auth.user_id}</span>
              <Badge variant="default" size="xs">{auth.broker}</Badge>
            </span>
          </Tooltip>
        ) : (
          <Button variant="primary" size="sm" onClick={onBrokerLogin}>
            <span className="hidden sm:inline">Login to&nbsp;</span>{brokerLabel}
          </Button>
        )}

        <IconAction label="Sign out of the panel" onClick={onLogout}>
          <LogOut className="h-4 w-4" />
        </IconAction>
      </div>
    </header>
  );
}
